/**
 * A6 — the per-format download step.
 *
 * Each format is its own page and its own page view: that is the acquisition
 * model paying for the free tier. So this page has one job and does it above
 * the fold — the Download button is at the top, and the preview is below it,
 * never the other way round. The file is generated in the browser from the
 * normalized transcript JSON; there is no route that returns a `.srt`.
 *
 * THE CHAIN, and every step is somebody else's engine:
 *
 *   ingestTranscript → resegment → write (bytes, and the preview)
 *
 * `ingestTranscript` returns a document with `cueCount: 0`, correctly and by
 * contract: Whisper's segments are model artefacts, not caption lines, and
 * cues exist only once `resegment` has rebuilt them.
 *
 * `resegmentOp` is CONVERT's and is imported, not authored. Its fifteen fields
 * are transcription-quality parameters — line-break vocabulary, min and max
 * duration, max CPS, min gap, the silence-split threshold, padding, the
 * no-speech cutoff — sourced field-by-field from CLEAN's constants. I refused
 * to guess them for a round, which kept this page dark; the answer existed in
 * `src/resegment/op.ts` and the barrel had not re-exported it. A barrel is the
 * module's API: what it does not export does not exist to a consumer.
 */

import type { DocumentId, Framerate, WriteFormat } from '../../../../../contracts/cue-model.ts';
import { resegmentOp } from '../../../../../src/resegment/index.ts';
import { labelled } from '../../../../../src/tools/ops-from-values.ts';
import { ApiError, getTranscript } from '../lib/api.ts';
import { watchCopies } from '../lib/copies.ts';
import { data, must, on, qs, qsa } from '../lib/dom.ts';
import { downloadChoices, mayDownload, renderOutcome } from '../lib/download-row.ts';
import { docxText, mountDocxLayout } from '../lib/docx.ts';
import { saveBytes } from '../lib/download.ts';
import { track } from '../lib/events.ts';
import { parseFramerate } from '../lib/option-panel.ts';
import { ensurePdfFonts } from '../lib/pdf-fonts.ts';
import { mountPdfLayout } from '../lib/pdf-layout.ts';
import {
  decodeWritten,
  labelWithEncoding,
  mountSaveEncoding,
  type SaveEncoding,
} from '../lib/save-encoding.ts';
import { countUse, usageFormat } from '../lib/usage.ts';
import { isVersionMismatch, WorkerFailure, worker } from '../lib/worker.ts';
import { downloadWriteOptions } from '../lib/write-options.ts';

/** This page's name in the usage counter (lib/usage.ts): one for every /t/{id}/{format}. */
const USAGE_TOOL = 'transcript';

export function mountFormatPage(): void {
  const root = qs<HTMLElement>('[data-format-page]');
  if (!root) return;
  const publicId = data(root, 'public-id');
  const format = data(root, 'format');
  if (!publicId || !format) return;

  const region = must<HTMLElement>('#banner-region');
  const button = qs<HTMLButtonElement>('[data-download]', root);
  const previewHost = qs<HTMLElement>('[data-format-preview]', root);
  const outcomeHost = qs<HTMLElement>('[data-download-outcome]', root);
  const available = data(root, 'available') !== 'false';
  // The preview is the written file's head: selecting it and copying is taking the result
  // (lib/copies.ts), counted once per page view beside the downloads.
  watchCopies([previewHost], USAGE_TOOL);

  let output: { bytes: Uint8Array | string; filename: string; encoding: SaveEncoding } | null =
    null;
  /** Set once the transcript is in the worker: a layout change writes the PDF again. */
  let rewrite: (() => void) | null = null;
  // A PDF's layout (lib/pdf-layout.ts), on /t/{id}/pdf: with times or text only. Mounted here,
  // before the button's handler, which reads it.
  const pdfLayout = mountPdfLayout(root, () => rewrite?.());
  pdfLayout.sync(format);

  if (button) {
    on(button, 'click', () => {
      // Synchronous from here to the anchor click: deferring a download past an
      // await is what makes it silently fail in Safari. The bytes were produced
      // before the button became enabled, precisely so this handler can be.
      if (output === null) return;
      saveBytes(output.bytes, output.filename, format, output.encoding);
      // The format and encoding the file was written in, and nothing about it.
      countUse('downloaded', USAGE_TOOL, usageFormat(format), output.encoding);
      // A PDF saved in the Text only layout (SEO_BRIEF_08 section 7.6), counted beside it.
      if (format === 'pdf' && pdfLayout.layout() === 'text') {
        countUse('pdf_layout_text_only', USAGE_TOOL, 'pdf');
      }
      track('format_download', { format });
    });
  }

  if (!available) {
    // The server already decided this: a caption-sourced transcript has no
    // per-word timing, so the word-level formats cannot be written. The chip
    // that led here is disabled too; this is the direct-navigation case.
    return;
  }

  const client = worker();
  const fpsPicker = qs<HTMLElement>('[data-fps-picker]', root);
  /** The document written, and the rate it was written at, for a rewrite. */
  let written: { docId: DocumentId; framerate: Framerate | null } | null = null;
  // The text encoding (lib/save-encoding.ts), on SMI, and on SRT once the
  // transcript turns out to be Korean. A change writes the file again, so the
  // count of characters EUC-KR can't hold and the preview are both current
  // before the button comes back.
  const saveEncoding = mountSaveEncoding(root, () => {
    if (written === null) return;
    output = null;
    if (button) button.disabled = true;
    void writeOut(written.docId, written.framerate).catch(failed);
  });
  saveEncoding.sync(format);
  // A Word document's layout (lib/docx.ts), on /t/{id}/docx: a table with
  // times or plain text. A change writes the file again, as above.
  const docxLayout = mountDocxLayout(root, () => {
    if (written === null) return;
    output = null;
    if (button) button.disabled = true;
    void writeOut(written.docId, written.framerate).catch(failed);
  });
  docxLayout.sync(format);
  rewrite = () => {
    if (written === null) return;
    output = null;
    if (button) button.disabled = true;
    void writeOut(written.docId, written.framerate).catch(failed);
  };
  /** The transcript's words, for the font a PDF of them needs (lib/pdf-fonts.ts). */
  let words: readonly string[] = [];

  /**
   * Write the transcript in this page's format and wire up the button.
   *
   * Separate from the ingest because a frame-based format (MicroDVD) is
   * written TWICE: once with no rate, which the writer refuses with
   * `framerate-required` rather than assume 23.976, and again with the rate
   * picked. `downloadWriteOptions` already took the override; nothing passed
   * one, so /t/{id}/sub only ever logged that failure (live at 31f48ec).
   */
  const writeOut = async (docId: DocumentId, framerate: Framerate | null): Promise<void> => {
    const encoding = saveEncoding.encodingFor(format);
    // Recorded before the write, so a pick made while it runs writes again.
    written = { docId, framerate };
    // A PDF of Polish, Russian or Greek words needs a font embedded in it, fetched once.
    if (format === 'pdf') await ensurePdfFonts(client, words);
    const result = await client.request('write', {
      docId,
      format: format as WriteFormat,
      options: downloadWriteOptions(format as WriteFormat, {
        ...(framerate === null ? {} : { framerate }),
        encoding,
        formatOptions: docxLayout.formatOptionsFor(format) ?? pdfLayout.formatOptionsFor(format),
      }),
    });
    // A newer pick of encoding started another write; this one answers the old.
    if (encoding !== saveEncoding.encodingFor(format)) return;
    // THE WARN-VS-REFUSE DECISION, and it needed no new protocol message.
    // `availability()` answers this for a whole format ROW before any write,
    // and is still unreachable from the main thread. But A6 is ONE format and
    // the write has already happened, so `WritePayload.loss` carries the same
    // `LossEntry[]` after the fact — and after the fact is soon enough on a
    // page that exists for exactly one format.
    const [choice] = downloadChoices([
      { format: format as WriteFormat, writable: true, loss: result.loss, blocking: false },
    ]);
    if (choice && outcomeHost) {
      renderOutcome(
        outcomeHost,
        choice,
        labelWithEncoding(formatLabel(root, format), encoding),
        encoding,
      );
    }

    // A refusal removes the button AND never assigns `output`, so the click
    // handler cannot fire even if something puts the button back. Two
    // independent reasons a wrong file cannot leave, because this is the one
    // thing on this page that must not go wrong quietly.
    if (choice && !mayDownload(choice)) {
      button?.remove();
      return;
    }

    // CORE names the file. `outputName` in core/adapter/filenames.ts already
    // applies the no-prefix, no-suffix, no-watermark rule.
    output = { bytes: result.output, filename: result.filename, encoding };
    if (button) button.disabled = false;

    // A6 wants "a preview of the first few cues rendered in that format".
    // The written bytes ARE that, so the preview costs no second pass — and
    // it is head-truncated rather than projected, because a `project` call
    // here would ask the worker to render the document twice for one screen.
    if (previewHost) {
      // Decoded in the encoding it was written in: EUC-KR bytes read as UTF-8
      // would preview as broken characters on the page meant to show they
      // are fine.
      //
      // A Word document is a ZIP, so its bytes read as text are a header and
      // XML; the preview is the document's own words instead.
      //
      // A PDF's bytes read as text are its object syntax, which is what this
      // pane showed on /t/{id}/pdf. Its words come from the same document
      // written as plain text, one more write in the worker for one screen.
      if (format === 'pdf') {
        const words = await client.request('write', {
          docId,
          format: 'txt',
          options: downloadWriteOptions('txt'),
        });
        if (written?.docId !== docId) return;
        previewHost.textContent = headOf(decodeWritten(words.output, 'utf-8'));
      } else {
        previewHost.textContent = headOf(previewText(result.output, encoding));
      }
    }
  };

  /** The writer asked for a rate: show the picker, and write again on a pick. */
  function askForRate(docId: DocumentId): void {
    if (!fpsPicker) return;
    fpsPicker.hidden = false;
    const chips = qsa<HTMLButtonElement>('[data-fps]', fpsPicker);
    for (const chip of chips) {
      on(chip, 'click', () => {
        const rate = labelled(parseFramerate(data(chip, 'fps') ?? ''));
        if (rate === null) return;
        for (const other of chips) other.setAttribute('aria-pressed', String(other === chip));
        // The bytes on the button were written at another rate, if at all.
        output = null;
        if (button) button.disabled = true;
        void writeOut(docId, rate).catch(failed);
      });
    }
    chips[0]?.focus();
  }

  function failed(err: unknown): void {
    if (err instanceof ApiError) {
      showProblemSafely(region, err);
      return;
    }
    if (isVersionMismatch(err)) {
      console.error('[format]', err);
      return;
    }
    if (err instanceof WorkerFailure) {
      console.error('[format] write failed', err.reason, err.detail);
      return;
    }
    console.error('[format]', err);
  }

  void getTranscript(publicId)
    .then(async (wire) => {
      // What the transcript says, before the first write reads the encoding:
      // /t/{id}/srt offers EUC-KR only for Korean. The segments are already on
      // this side of the boundary, so it costs no request.
      saveEncoding.setText(wire.segments.map((segment) => segment.text));
      words = wire.segments.map((segment) => segment.text);
      // C1. The WIRE JSON goes over verbatim and the worker does both steps —
      // wireToTranscript and documentFromTranscript — because both mint ids,
      // and minting outside the worker is the second id authority C12 removed.
      const ingested = await client.request('ingestTranscript', {
        wire,
        inputName: data(root, 'basename') ?? publicId,
      });

      // cueCount is 0 here, by contract: segments are model artefacts, not
      // caption lines. Cues exist only after resegment runs.
      //
      // The op is CONVERT's, not mine. Its fifteen fields are transcription
      // quality parameters sourced field-by-field from CLEAN's constants; the
      // language selects per-language line-break profiles, so a CJK transcript
      // breaks on CJK rules rather than on Latin ones. The ingest response
      // carries it, so passing it costs nothing.
      const segmented = await client.request('resegment', {
        docId: ingested.docId,
        op: resegmentOp(ingested.trackId, ingested.language),
      });

      try {
        await writeOut(segmented.docId, null);
      } catch (err) {
        if (
          err instanceof WorkerFailure &&
          (err.reason === 'framerate-required' || err.reason === 'needs-framerate')
        ) {
          askForRate(segmented.docId);
          return;
        }
        throw err;
      }
    })
    .catch(failed);
}

/** What the preview pane shows for written output: the file, or a Word document's words. */
export function previewText(output: Uint8Array | string, encoding: SaveEncoding): string {
  if (typeof output !== 'string') {
    const words = docxText(output);
    if (words !== null) return words;
  }
  return decodeWritten(output, encoding);
}

/** Kept separate so the import graph of this page stays obvious. */
function showProblemSafely(region: HTMLElement, err: ApiError): void {
  void import('../lib/states.ts').then((states) => states.showProblem(region, err.problem));
}

/**
 * The first few cues of a rendered file, for the preview pane.
 *
 * Line-based rather than cue-based on purpose: this runs over every format,
 * and counting cues would mean knowing each format's record separator — which
 * is parsing, and is not this side of the boundary.
 */
export function headOf(text: string, lines = 24, chars = 1200): string {
  const head = text.split('\n').slice(0, lines).join('\n');
  return head.length > chars ? `${head.slice(0, chars)}…` : head;
}

/**
 * The format's own name, from the page's <h1>, never from a table in here.
 *
 * `format_label` is server-side context the founder approves; duplicating it
 * into a client-side map is how "MicroDVD SUB" becomes "SUB" on one surface
 * and not the other.
 */
function formatLabel(root: HTMLElement, fallback: string): string {
  return labelFromHeading(qs<HTMLElement>('h1', root)?.textContent, fallback);
}

/**
 * `Download SRT` -> `SRT`, the heading format.html renders, minus its verb.
 *
 * Pure and exported because the regex was wrong for as long as it existed:
 * `/^Downloads+/` (a lost backslash, still in the live boot.js on 2026-10-05)
 * needs an "s" after "Download", so it never matched and the loss banner read
 * "Download SRT can't carry everything in this file".
 */
export function labelFromHeading(heading: string | null | undefined, fallback: string): string {
  const text = heading?.replace(/^\s*Download\s+/, '').trim();
  return text && text !== '' ? text : fallback.toUpperCase();
}
